import * as THREE from 'three';
import * as CANNON from 'cannon';

export class PlayerUI {
    constructor(player) {
        this.player = player; // Reference to the Player instance
        this.jumpChargeContainer = null; // Store container reference
        this.debugStatsContainer = null; // Store debug stats container reference
        this.gameRef = null; // Reference to the Game instance for camera values
    }
    
    // Set game reference for accessing camera values
    setGameReference(game) {
        this.gameRef = game;
    }

    // Create UI for jump charge
    createJumpChargeUI() {
        // Remove any existing UI
        const existingUI = document.getElementById('jump-charge-container');
        if (existingUI) {
            existingUI.remove();
        }
        
        // Create container
        const container = document.createElement('div');
        container.id = 'jump-charge-container';
        container.style.position = 'fixed';
        container.style.bottom = '20px';
        container.style.left = '50%';
        container.style.transform = 'translateX(-50%)';
        container.style.width = '200px';
        container.style.height = '20px';
        container.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        container.style.borderRadius = '10px';
        container.style.padding = '3px';
        container.style.display = 'none'; // Initially hidden
        
        // Create the charge bar
        const chargeBar = document.createElement('div');
        chargeBar.id = 'jump-charge-bar';
        chargeBar.style.width = '0%';
        chargeBar.style.height = '100%';
        chargeBar.style.backgroundColor = '#5af';
        chargeBar.style.borderRadius = '7px';
        chargeBar.style.transition = 'width 0.05s linear';
        
        // Create the sweet spot indicator (rechts)
        const sweetSpotIndicator = document.createElement('div');
        sweetSpotIndicator.id = 'sweet-spot-indicator';
        sweetSpotIndicator.style.position = 'absolute';
        sweetSpotIndicator.style.top = '0';
        sweetSpotIndicator.style.left = `${this.player.jumpChargeSweetSpotMin * 100}%`;
        sweetSpotIndicator.style.width = `${(this.player.jumpChargeSweetSpotMax - this.player.jumpChargeSweetSpotMin) * 100}%`;
        sweetSpotIndicator.style.height = '100%';
        sweetSpotIndicator.style.backgroundColor = 'rgba(0, 255, 0, 0.2)';
        sweetSpotIndicator.style.borderRadius = '7px';
        sweetSpotIndicator.style.pointerEvents = 'none';
        
        // Create the end zone indicator (mitte)
        const endZoneIndicator = document.createElement('div');
        endZoneIndicator.id = 'end-zone-indicator';
        endZoneIndicator.style.position = 'absolute';
        endZoneIndicator.style.top = '0';
        endZoneIndicator.style.left = `${(this.player.jumpChargeEndZone - this.player.jumpChargeEndZoneRange/2) * 100}%`;
        endZoneIndicator.style.width = `${this.player.jumpChargeEndZoneRange * 100}%`;
        endZoneIndicator.style.height = '100%';
        endZoneIndicator.style.backgroundColor = 'rgba(255, 165, 0, 0.2)'; // Orange für die Endzone
        endZoneIndicator.style.borderRadius = '7px';
        endZoneIndicator.style.pointerEvents = 'none';
        
        // Add elements to DOM
        container.appendChild(endZoneIndicator);
        container.appendChild(sweetSpotIndicator);
        container.appendChild(chargeBar);
        document.body.appendChild(container);
        
        this.jumpChargeContainer = container; // Save reference
    }

    // Update the jump charge UI
    updateJumpChargeUI() {
        const container = document.getElementById('jump-charge-container');
        const chargeBar = document.getElementById('jump-charge-bar');
        
        if (!container || !chargeBar) return;
        
        if (this.player.isChargingJump) {
            container.style.display = 'block';
            
            // Update the fill amount
            chargeBar.style.width = `${this.player.jumpChargeAmount * 100}%`;
            
            // Update color based on charge level
            if (this.player.jumpChargeAmount >= this.player.jumpChargeSweetSpotMin && 
                this.player.jumpChargeAmount <= this.player.jumpChargeSweetSpotMax) {
                // In sweet spot - green
                chargeBar.style.backgroundColor = '#5f5';
            } else if (Math.abs(this.player.jumpChargeAmount - this.player.jumpChargeEndZone) <= this.player.jumpChargeEndZoneRange/2) {
                // In end zone - orange
                chargeBar.style.backgroundColor = '#fa5';
            } else {
                // Normal zone - blue
                chargeBar.style.backgroundColor = '#5af';
            }
        } else {
            container.style.display = 'none';
        }
    }

    // Create debug stats UI
    createDebugStatsUI() {
        // Remove any existing UI
        const existingUI = document.getElementById('debug-stats-container');
        if (existingUI) {
            existingUI.remove();
        }
        
        // Create container
        const container = document.createElement('div');
        container.id = 'debug-stats-container';
        container.style.position = 'fixed';
        container.style.top = '10px';
        container.style.left = '10px';
        container.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        container.style.color = '#fff';
        container.style.padding = '10px';
        container.style.borderRadius = '5px';
        container.style.fontFamily = 'monospace';
        container.style.fontSize = '14px';
        container.style.zIndex = '1000';
        container.style.display = this.player.showDebugStats ? 'block' : 'none';
        
        // Create content elements
        const title = document.createElement('div');
        title.textContent = 'Debug Statistics';
        title.style.fontWeight = 'bold';
        title.style.marginBottom = '8px';
        title.style.borderBottom = '1px solid #aaa';
        title.style.paddingBottom = '4px';
        
        const orientation = document.createElement('div');
        orientation.id = 'player-orientation';
        orientation.textContent = 'Orientation: 0°';
        
        const position = document.createElement('div');
        position.id = 'contact-position';
        position.textContent = 'Position: X: 0.00, Y: 0.00, Z: 0.00';
        
        const verticalSpeed = document.createElement('div');
        verticalSpeed.id = 'vertical-speed';
        verticalSpeed.textContent = 'Speed (vertical): 0.00 m/s';
        
        const horizontalSpeed = document.createElement('div');
        horizontalSpeed.id = 'horizontal-speed';
        horizontalSpeed.textContent = 'Speed (horizontal): 0.00 m/s';
        
        const downhillSpeed = document.createElement('div');
        downhillSpeed.id = 'downhill-speed';
        downhillSpeed.textContent = 'Downhill Boost: 0.00 m/s';
        
        const totalSpeed = document.createElement('div');
        totalSpeed.id = 'total-speed';
        totalSpeed.textContent = 'Total Speed: 0.00 m/s';
        totalSpeed.style.fontWeight = 'bold';
        
        const slope = document.createElement('div');
        slope.id = 'slope-angle';
        slope.textContent = 'Slope Angle: 0.00°';
        
        // Add camera position info
        const cameraInfo = document.createElement('div');
        cameraInfo.style.marginTop = '10px';
        cameraInfo.style.borderTop = '1px dashed #aaa';
        cameraInfo.style.paddingTop = '5px';
        cameraInfo.style.fontWeight = 'bold';
        cameraInfo.textContent = 'Camera Information';
        
        const cameraY = document.createElement('div');
        cameraY.id = 'camera-y-position';
        cameraY.textContent = 'Camera Y Offset: 0.00';
        
        const cameraZ = document.createElement('div');
        cameraZ.id = 'camera-z-position';
        cameraZ.textContent = 'Camera Z Distance: 0.00';
        
        const downhillFactor = document.createElement('div');
        downhillFactor.id = 'downhill-factor';
        downhillFactor.textContent = 'Downhill Factor: 0.00';
        
        // Add ground check status element
        const groundStatus = document.createElement('div');
        groundStatus.id = 'ground-check-status';
        groundStatus.textContent = 'Ground Check: Not Grounded';
        groundStatus.style.marginTop = '10px';
        groundStatus.style.fontWeight = 'bold';
        
        // Add jump charge status
        const jumpStatus = document.createElement('div');
        jumpStatus.id = 'jump-charge-status';
        jumpStatus.textContent = 'Jump: Not Charged';
        
        // Add elements to container
        container.appendChild(title);
        container.appendChild(orientation);
        container.appendChild(position);
        container.appendChild(verticalSpeed);
        container.appendChild(horizontalSpeed);
        container.appendChild(downhillSpeed);
        container.appendChild(totalSpeed);
        container.appendChild(slope);
        container.appendChild(cameraInfo);
        container.appendChild(cameraY);
        container.appendChild(cameraZ);
        container.appendChild(downhillFactor);
        container.appendChild(groundStatus);
        container.appendChild(jumpStatus);
        
        // Add to DOM
        document.body.appendChild(container);
        
        // Save reference
        this.debugStatsContainer = container;
    }

    // Update debug stats UI
    updateDebugStatsUI() {
        if (!this.player.showDebugStats) {
            if (this.debugStatsContainer) {
                this.debugStatsContainer.style.display = 'none';
            }
            return;
        }
        
        if (!this.debugStatsContainer) {
            this.createDebugStatsUI();
        } else {
            this.debugStatsContainer.style.display = 'block';
        }
        
        // Calculate all required values
        
        // 1. Orientation (angle in degrees around Y axis, normalized to 0-360°)
        let orientationRad = this.player.rotation.y;
        // Normalize to 0-2π range
        orientationRad = THREE.MathUtils.euclideanModulo(orientationRad, Math.PI * 2);
        const orientationDeg = THREE.MathUtils.radToDeg(orientationRad);
        const orientationFormat = orientationDeg.toFixed(2);
        
        // 2. Contact point position (lowest point of collision sphere)
        let contactPointX = 0, contactPointY = 0, contactPointZ = 0;
        
        if (this.player.rigidBody && this.player.shape instanceof CANNON.Sphere) {
            // Calculate the lowest point of the sphere
            const downDirection = new THREE.Vector3(0, -1, 0);
            const sphereCenter = new THREE.Vector3(
                this.player.rigidBody.position.x,
                this.player.rigidBody.position.y,
                this.player.rigidBody.position.z
            );
            
            this.player.contactPoint.copy(sphereCenter);
            downDirection.normalize();
            downDirection.multiplyScalar(this.player.shape.radius);
            this.player.contactPoint.add(downDirection);
            
            contactPointX = this.player.contactPoint.x.toFixed(2);
            contactPointY = this.player.contactPoint.y.toFixed(2);
            contactPointZ = this.player.contactPoint.z.toFixed(2);
        }
        
        // 3. Vertical speed
        const verticalSpeedValue = this.player.rigidBody ? this.player.rigidBody.velocity.y.toFixed(2) : '0.00';
        
        // 4. Horizontal speed
        const horizontalSpeed = this.player.currentSpeed.toFixed(2);
        
        // 5. Downhill speed
        const downhillSpeed = this.player.downhillSpeed.toFixed(2);
        
        // 6. Total speed
        const totalSpeed = (this.player.currentSpeed + this.player.downhillSpeed).toFixed(2);
        
        // 7. Slope angle - improved calculation for better accuracy
        let slopeAngleStr = '0.00';
        if (this.player.rigidBody && this.player.physicsWorld) {
            // Start the ray from the contact point (bottom of sphere)
            const start = new CANNON.Vec3(
                this.player.contactPoint.x,
                this.player.contactPoint.y + 0.05, // Start slightly above contact point
                this.player.contactPoint.z
            );
            
            // End point slightly below the contact point
            const end = new CANNON.Vec3(
                this.player.contactPoint.x,
                this.player.contactPoint.y - 0.5, // Check slightly below
                this.player.contactPoint.z
            );
            
            const result = new CANNON.RaycastResult();
            this.player.physicsWorld.raycastClosest(start, end, {
                skipBackfaces: true, // Don't detect backfaces
                collisionFilterMask: -1, // Detect all objects
                collisionFilterGroup: -1
            }, result);
            
            if (result.hasHit) {
                // Get the hit normal
                const hitNormal = new THREE.Vector3(
                    result.hitNormalWorld.x,
                    result.hitNormalWorld.y,
                    result.hitNormalWorld.z
                );
                
                const upVector = new THREE.Vector3(0, 1, 0);
                
                // Check if the surface is nearly flat by comparing normal with up vector
                // cos(0°) = 1, cos(5°) ≈ 0.996
                const surfaceNormalDotUp = hitNormal.dot(upVector);
                const isFlat = surfaceNormalDotUp > 0.996; // Within ~5 degrees of flat
                
                // Maximum slope angle (when facing directly up/downhill)
                const maxSlopeAngle = 90 - Math.acos(surfaceNormalDotUp) * (180 / Math.PI);
                
                // If we're on a nearly flat surface, slope angle is always 0
                // regardless of orientation
                if (isFlat) {
                    this.player.slopeAngle = 0;
                } else {
                    // Calculate the steepest descent direction
                    // We need the direction vector that points downhill (perpendicular to the normal and on the slope)
                    const steepestDescent = new THREE.Vector3();
                    // Cross product of the normal with the world up to get a vector perpendicular to both
                    steepestDescent.crossVectors(new THREE.Vector3(1, 0, 0), hitNormal).normalize();
                    // If the result is nearly zero (normal is parallel to x-axis), use z-axis instead
                    if (steepestDescent.lengthSq() < 0.01) {
                        steepestDescent.crossVectors(new THREE.Vector3(0, 0, 1), hitNormal).normalize();
                    }
                    // Now cross the normal with this perpendicular vector to get the steepest descent direction
                    steepestDescent.crossVectors(hitNormal, steepestDescent).normalize();
                    // Make sure it points downhill (negative Y component)
                    if (steepestDescent.y > 0) {
                        steepestDescent.multiplyScalar(-1);
                    }
                    
                    // Get player's forward direction based on orientation
                    const playerForward = new THREE.Vector3(0, 0, 1);
                    playerForward.applyEuler(this.player.rotation);
                    
                    // Project steepest descent to horizontal plane
                    const horizontalDescent = new THREE.Vector3(steepestDescent.x, 0, steepestDescent.z).normalize();
                    const horizontalForward = new THREE.Vector3(playerForward.x, 0, playerForward.z).normalize();
                    
                    // Calculate angle between player's forward direction and steepest descent on horizontal plane
                    // This tells us whether player is facing directly downhill, uphill, or sideways
                    let directionAngle = Math.acos(horizontalForward.dot(horizontalDescent));
                    
                    // Handle special case for NaN (identical or opposite vectors)
                    if (isNaN(directionAngle)) {
                        directionAngle = horizontalForward.dot(horizontalDescent) > 0 ? 0 : Math.PI;
                    }
                    
                    // Convert to degrees
                    directionAngle = THREE.MathUtils.radToDeg(directionAngle);
                    
                    // Calculate actual slope angle based on direction
                    // When directionAngle is 0, facing downhill = negative slope
                    // When directionAngle is 90, facing sideways = zero slope
                    // When directionAngle is 180, facing uphill = positive slope
                    let slopeAngle = 0;
                    
                    if (directionAngle <= 90) {
                        // Facing between downhill and sideways: negative to zero
                        slopeAngle = -maxSlopeAngle * (1 - directionAngle / 90);
                    } else {
                        // Facing between sideways and uphill: zero to positive
                        slopeAngle = maxSlopeAngle * ((directionAngle - 90) / 90);
                    }
                    
                    // Save the calculated slope angle
                    this.player.slopeAngle = slopeAngle;
                }
                
                slopeAngleStr = Math.abs(this.player.slopeAngle).toFixed(2);
                
                // Add sign for display
                slopeAngleStr = (this.player.slopeAngle >= 0 ? '+' : '-') + slopeAngleStr;
            } else {
                // If no raycast hit, assume we're on flat ground
                this.player.slopeAngle = 0;
                slopeAngleStr = '+0.00';
                
                // Remove debug visuals if no hit
                if (this.player.debugHitPoint) {
                    const scene = this.player.mesh.parent;
                    if (scene && this.player.debugHitPoint.parent === scene) {
                        scene.remove(this.player.debugHitPoint);
                    }
                    this.player.debugHitPoint = null;
                }
                if (this.player.debugNormal) {
                    const scene = this.player.mesh.parent;
                    if (scene && this.player.debugNormal.parent === scene) {
                        scene.remove(this.player.debugNormal);
                    }
                    this.player.debugNormal = null;
                }
            }
        }
        
        // 8. Ground check status
        const groundStatusEl = document.getElementById('ground-check-status');
        if (groundStatusEl) {
            const groundStatus = this.player.isGrounded ? 'GROUNDED' : 'NOT GROUNDED';
            const statusColor = this.player.isGrounded ? '#5f5' : '#f55'; // Green if grounded, red if not
            groundStatusEl.innerHTML = `Ground Check: <span style="color: ${statusColor}; font-weight: bold;">${groundStatus}</span>`;
        }
        
        // 9. Jump charge status
        const jumpStatusEl = document.getElementById('jump-charge-status');
        if (jumpStatusEl) {
            let status = 'Not Charged';
            let statusColor = '#aaa';
            
            if (this.player.isChargingJump) {
                status = 'CHARGING';
                statusColor = '#aaf';
            } else if (this.player.jumpCharged) {
                status = `CHARGED (${this.player.calculatedJumpForce.toFixed(1)})`;
                if (this.player.spaceKeyWasReleased) {
                    status += ' - READY';
                    statusColor = '#5f5';
                } else {
                    status += ' - HOLDING';
                    statusColor = '#f5f';
                }
            }
            
            jumpStatusEl.innerHTML = `Jump: <span style="color: ${statusColor};">${status}</span>`;
        }
        
        // Update UI elements
        document.getElementById('player-orientation').textContent = `Orientation: ${orientationFormat}°`;
        document.getElementById('contact-position').textContent = `Position: X: ${contactPointX}, Y: ${contactPointY}, Z: ${contactPointZ}`;
        document.getElementById('vertical-speed').textContent = `Speed (vertical): ${verticalSpeedValue} m/s`;
        document.getElementById('horizontal-speed').textContent = `Speed (horizontal): ${horizontalSpeed} m/s`;
        document.getElementById('downhill-speed').textContent = `Downhill Boost: ${downhillSpeed} m/s`;
        document.getElementById('total-speed').textContent = `Total Speed: ${totalSpeed} m/s`;
        document.getElementById('slope-angle').textContent = `Slope Angle: ${slopeAngleStr}°`;
        
        // Update camera information if game reference is available
        if (this.gameRef) {
            const cameraYEl = document.getElementById('camera-y-position');
            const cameraZEl = document.getElementById('camera-z-position');
            const downhillFactorEl = document.getElementById('downhill-factor');
            
            if (cameraYEl && cameraZEl && downhillFactorEl) {
                // Get the current dynamic camera values if available
                if (this.gameRef.followPlayerDirection && this.player.downhillSpeed !== undefined) {
                    // Calculate the same values used in the Game.animate method
                    const playerSpeed = Math.abs(this.player.currentSpeed) + Math.abs(this.player.downhillSpeed);
                    const speedFactor = Math.min(playerSpeed / this.gameRef.maxSpeedForCamera, 1.0);
                    const downhillFactor = Math.min(this.player.downhillSpeed / this.player.maxDownhillBoost, 1.0);
                    
                    // For Y, combine regular speed factor with downhill factor
                    const maxYWithDownhill = this.gameRef.maxCameraY + 
                        (this.gameRef.maxCameraYWithDownhill - this.gameRef.maxCameraY) * downhillFactor;
                    const dynamicCameraY = this.gameRef.minCameraY + 
                        (maxYWithDownhill - this.gameRef.minCameraY) * speedFactor;
                    const dynamicCameraZ = this.gameRef.minCameraZ + 
                        (this.gameRef.maxCameraZ - this.gameRef.minCameraZ) * speedFactor;
                    
                    cameraYEl.textContent = `Camera Y Offset: ${dynamicCameraY.toFixed(2)}`;
                    cameraZEl.textContent = `Camera Z Distance: ${dynamicCameraZ.toFixed(2)}`;
                    downhillFactorEl.textContent = `Downhill Factor: ${downhillFactor.toFixed(2)}`;
                } else {
                    cameraYEl.textContent = `Camera Y Offset: ${this.gameRef.cameraOffset.y.toFixed(2)}`;
                    cameraZEl.textContent = `Camera Z Distance: ${this.gameRef.cameraOffset.z.toFixed(2)}`;
                    downhillFactorEl.textContent = `Downhill Factor: 0.00`;
                }
            }
        }
    }
}
